/**
 * ENG-6088 — bounded auto-resume for circuit-breaker pauses.
 *
 * A tripped breaker (ENG-5441) pauses the agent until an operator clicks
 * Resume — every paused-agent hour is an hour of zero Conversation scores.
 * This module decides, on each manager poll, whether a tripped agent has
 * earned ONE automatic resume:
 *
 *   - the trip driver must have gone quiet: no restart event for
 *     `quietMs` (default 30 min). While paused the manager spawns nothing,
 *     so "quiet" is effectively time-since-trip — the resume is a bounded
 *     probe: if the driver storms again, the breaker re-trips within its
 *     own window and the agent stays paused.
 *   - one credit per trip cycle: after an auto-resume, a re-trip is NOT
 *     auto-resumed again until either an operator resumes (clears the
 *     marker — deliberate human intent resets the credit) or the backoff
 *     TTL (`backoffResetMs`, default 24h) expires. The TTL exists so a
 *     genuinely-unrelated trip weeks later isn't hostage to a marker from
 *     a long-resolved incident, while still bounding unattended resumes
 *     to at most one per TTL window.
 *
 * ENG-7577 narrows WHICH trips this blind path may probe: provisioning-reload
 * trips only. A blind timer has no evidence the driver recovered, which is a
 * reasonable bet on a self-limiting operator action (wiring integrations onto a
 * new agent) and a bad one on a crash loop. Crash-class trips keep requiring
 * manual operator clearance — or the ENG-6383 resume-reconciler, which is
 * class-blind precisely because it proves recovery instead of assuming it. See
 * {@link decideAutoResume}.
 *
 * Pure state machine, no I/O — manager-worker.ts owns the API call, the
 * marker map, and its serialisation into manager-state.json (same pattern
 * as RestartBreaker). Tests drive `now` directly.
 *
 * Rollout: gated OFF by default via the `auto-resume-enabled` registry flag
 * (ENG-8395), with `AGT_AUTO_RESUME_ENABLED` retained as the highest-precedence
 * host-local override. It was env-ONLY until ENG-8395, and that could not express
 * the canary-then-fleet rollout its own docs prescribed: the env var lives in the
 * host env file, so its grain is per-HOST — flipping it on agt-aws-1 armed all 14
 * agents at once, a fleet flip wearing a canary's clothes. The registry flag
 * resolves per org/team/host/agent, so one agent genuinely goes first. Its direct
 * sibling in this same recovery path, `resume-reconciler`, was promoted in June
 * (ENG-6395); this one was left behind.
 */

import type { TripState } from './restart-breaker.js';
import { readEnvNumber, tripClass } from './restart-breaker.js';

/** Persisted per-agent marker: this trip cycle already used its credit. */
export interface AutoResumeRecord {
  /** trippedAt of the trip that was auto-resumed (forensics + dedupe). */
  trippedAt: number;
  /** When the manager issued the auto-resume. TTL anchor. */
  autoResumedAt: number;
}

export interface AutoResumeConfig {
  enabled: boolean;
  /** Driver must be quiet this long before the one auto-resume fires. */
  quietMs: number;
  /** A used credit expires after this long, re-arming auto-resume. */
  backoffResetMs: number;
}

const DEFAULT_QUIET_MS = 1_800_000; // 30 min
const DEFAULT_BACKOFF_RESET_MS = 86_400_000; // 24 h

/**
 * ENG-8395 — registry key for the flag that gates this module. Resolved by the
 * caller through `HostFlagStore.getBooleanForAgent`, which is what makes the
 * flip agent-grained; this module stays pure and takes the resolved boolean.
 */
export const AUTO_RESUME_FLAG_KEY = 'auto-resume-enabled';

/**
 * ENG-8395 — parse `AGT_AUTO_RESUME_ENABLED` into an explicit TRI-state.
 *
 * `undefined` means "the operator expressed no opinion", and it is the whole
 * point: a boolean-only reader cannot tell *unset* from *off*, so it can only
 * ever mask the registry flag. Returning undefined for an unset (or
 * unrecognised) value is what lets the flag be the fallback layer.
 *
 * Accepts a slightly WIDER truthy/falsy vocabulary than the registry's
 * `coerceEnvValue` (which takes only true/1/false/0): the legacy yes/on
 * spellings were accepted here before ENG-8395 and dropping them would silently
 * disarm a host that used one. The two agree on every value coerceEnvValue
 * recognises, and this reader is the authority for this gate — the flag store's
 * env layer only ever supplies the fallback value.
 */
export function readAutoResumeEnvOverride(
  env: NodeJS.ProcessEnv = process.env,
): boolean | undefined {
  const raw = (env['AGT_AUTO_RESUME_ENABLED'] ?? '').trim().toLowerCase();
  if (raw === '') return undefined;
  if (raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on') return true;
  if (raw === '0' || raw === 'false' || raw === 'no' || raw === 'off') return false;
  // Unrecognised spelling: treat as no opinion rather than as `false`, so a
  // typo in the host env file cannot silently pin a flag-armed agent off.
  return undefined;
}

/**
 * Resolve the auto-resume config. `enabled` follows the ENG-8395 three-way
 * precedence:
 *
 *   env override (set)  ->  `flagEnabled` (registry flag)  ->  false
 *
 * `flagEnabled` is the caller's already-resolved value for
 * {@link AUTO_RESUME_FLAG_KEY} — see manager-worker, which resolves it per
 * agent. Omitting it (tests, any non-manager caller) reproduces the pre-ENG-8395
 * env-only behaviour exactly.
 *
 * NOTE: `opts.env` scopes the ENABLED lookup only. The two numeric tunables go
 * through `readEnvNumber`, which reads `process.env` directly — unchanged from
 * before, and deliberately not widened here.
 */
export function readAutoResumeConfig(
  opts: { flagEnabled?: boolean; env?: NodeJS.ProcessEnv } = {},
): AutoResumeConfig {
  return {
    enabled: readAutoResumeEnvOverride(opts.env ?? process.env) ?? opts.flagEnabled ?? false,
    quietMs: readEnvNumber('AGT_AUTO_RESUME_QUIET_MS', DEFAULT_QUIET_MS),
    backoffResetMs: readEnvNumber('AGT_AUTO_RESUME_BACKOFF_RESET_MS', DEFAULT_BACKOFF_RESET_MS),
  };
}

export type AutoResumeDecision =
  | { action: 'resume'; quietMs: number }
  | {
      action: 'skip';
      reason: 'disabled' | 'no-trip' | 'class-not-eligible' | 'already-auto-resumed' | 'not-quiet-yet';
      /** For 'not-quiet-yet': ms until the quiet threshold is reached. */
      remainingMs?: number;
    };

/**
 * Decide whether a tripped, paused agent should be auto-resumed now.
 *
 * `marker` is the persisted {@link AutoResumeRecord} for this agent (if
 * any). A live marker means the credit is spent — stay paused until an
 * operator resumes or the TTL re-arms it. The caller must clear the marker
 * on operator resume (paused → active not initiated by us).
 *
 * ENG-7577 — CLASS GATE. This is the BLIND path: it resumes on a quiet timer,
 * with no evidence the thing that drove the restarts actually recovered. That is
 * an acceptable probe for a **provisioning-reload** trip, whose cause is a known,
 * self-limiting operator action (wiring integrations onto an agent) that ends by
 * itself — the very population ENG-7812 showed still trips, because a brand-new
 * agent has no prior integration set to bucket a membership delta against, so
 * every provisioning event lands in the shared unattributed bucket. It is NOT
 * acceptable for a **crash-class** trip, where the blind timer would hand a
 * structurally-stuck agent an unattended resume straight back into its loop.
 *
 * So: a crash-class trip is never blind-resumed and continues to require manual
 * operator clearance (or the ENG-6383 resume-reconciler, which is class-blind by
 * design precisely because it does NOT resume blind — it proves the dependency
 * recovered first). `credential-rotation` cannot reach here: that class is
 * recorded but never trips on its own.
 */
export function decideAutoResume(opts: {
  trip: TripState | undefined;
  marker: AutoResumeRecord | undefined;
  config: AutoResumeConfig;
  now: number;
}): AutoResumeDecision {
  const { trip, marker, config, now } = opts;
  if (!config.enabled) return { action: 'skip', reason: 'disabled' };
  if (!trip) return { action: 'skip', reason: 'no-trip' };

  // Class gate BEFORE the credit gate: a crash-class trip must not consume,
  // spend or otherwise interact with the one-per-window credit it can never use.
  if (tripClass(trip) !== 'provisioning') {
    return { action: 'skip', reason: 'class-not-eligible' };
  }

  if (marker && now - marker.autoResumedAt < config.backoffResetMs) {
    return { action: 'skip', reason: 'already-auto-resumed' };
  }

  // Quiet anchor: the most recent restart event the breaker saw (falls
  // back to trippedAt — hydrated trips always carry it). While paused no
  // sessions run, so no further events accrue and this is monotone.
  const lastEventAt = trip.eventsAtTrip.length > 0
    ? Math.max(trip.trippedAt, ...trip.eventsAtTrip.map((e) => e.at))
    : trip.trippedAt;
  const quietFor = now - lastEventAt;
  if (quietFor < config.quietMs) {
    return { action: 'skip', reason: 'not-quiet-yet', remainingMs: config.quietMs - quietFor };
  }

  return { action: 'resume', quietMs: quietFor };
}

/**
 * Rehydrate persisted markers from manager-state.json. Mirrors
 * RestartBreaker.hydrate's defensive shape-check — a corrupt entry is
 * dropped rather than poisoning the map.
 */
export function hydrateAutoResumeMarkers(
  saved: Record<string, AutoResumeRecord> | null | undefined,
): Map<string, AutoResumeRecord> {
  const map = new Map<string, AutoResumeRecord>();
  if (!saved) return map;
  for (const [codeName, rec] of Object.entries(saved)) {
    if (rec && typeof rec.trippedAt === 'number' && typeof rec.autoResumedAt === 'number') {
      map.set(codeName, rec);
    }
  }
  return map;
}
